import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";
import { forwardRef } from "react";

export function InputGroup({
	children,
}: React.ComponentPropsWithoutRef<"span">) {
	return (
		<span
			data-slot="control"
			className={clsx(
				"relative isolate block",
				"has-[[data-slot=icon]:first-child]:[&_input]:pl-10 has-[[data-slot=icon]:last-child]:[&_input]:pr-10 sm:has-[[data-slot=icon]:first-child]:[&_input]:pl-8 sm:has-[[data-slot=icon]:last-child]:[&_input]:pr-8",
				"*:data-[slot=icon]:pointer-events-none *:data-[slot=icon]:absolute *:data-[slot=icon]:top-3 *:data-[slot=icon]:z-10 *:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:top-2.5 sm:*:data-[slot=icon]:size-4",
				"[&>[data-slot=icon]:first-child]:left-3 sm:[&>[data-slot=icon]:first-child]:left-2.5 [&>[data-slot=icon]:last-child]:right-3 sm:[&>[data-slot=icon]:last-child]:right-2.5",
				"*:data-[slot=icon]:text-base-content/50",
			)}
		>
			{children}
		</span>
	);
}

const dateTypes = ["date", "datetime-local", "month", "time", "week"];
type DateType = (typeof dateTypes)[number];

export const Input = forwardRef(function Input(
	{
		className,
		...props
	}: {
		className?: string;
		type?:
			| "email"
			| "number"
			| "password"
			| "search"
			| "tel"
			| "text"
			| "url"
			| DateType;
	} & Omit<Headless.InputProps, "as" | "className">,
	ref: React.ForwardedRef<HTMLInputElement>,
) {
	return (
		<span
			data-slot="control"
			className={clsx([
				className,
				// Basic layout
				"relative block w-full",
				// Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
				"before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-base-100 before:shadow-sm",
				// Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
				"dark:before:hidden",
				// Focus ring
				"after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-primary",
				// Disabled state
				"has-data-disabled:opacity-50 has-data-disabled:before:bg-base-content/5 has-data-disabled:before:shadow-none",
			])}
		>
			<Headless.Input
				ref={ref}
				{...props}
				className={clsx([
					// Date classes
					props.type &&
						dateTypes.includes(props.type) && [
							"[&::-webkit-datetime-edit-fields-wrapper]:p-0",
							"[&::-webkit-date-and-time-value]:min-h-[1.5em]",
							"[&::-webkit-datetime-edit]:inline-flex",
							"[&::-webkit-datetime-edit]:p-0",
							"[&::-webkit-datetime-edit-year-field]:p-0",
							"[&::-webkit-datetime-edit-month-field]:p-0",
							"[&::-webkit-datetime-edit-day-field]:p-0",
							"[&::-webkit-datetime-edit-hour-field]:p-0",
							"[&::-webkit-datetime-edit-minute-field]:p-0",
							"[&::-webkit-datetime-edit-second-field]:p-0",
							"[&::-webkit-datetime-edit-millisecond-field]:p-0",
							"[&::-webkit-datetime-edit-meridiem-field]:p-0",
						],
					// Basic layout
					"relative block w-full appearance-none rounded-lg px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
					// Typography
					"text-base/6 text-base-content placeholder:text-base-content/50 sm:text-sm/6",
					// Border
					"border border-base-content/10 data-hover:border-base-content/20",
					// Background color
					"bg-transparent dark:bg-base-content/5",
					// Hide default focus styles
					"focus:outline-hidden",
					// Invalid state
					"data-invalid:border-error data-invalid:data-hover:border-error",
					// Disabled state
					"data-disabled:border-base-content/20 dark:data-disabled:border-base-content/15 dark:data-disabled:bg-base-content/2.5 dark:data-hover:data-disabled:border-base-content/15",
					// System icons
					"dark:scheme-dark",
				])}
			/>
		</span>
	);
});
